import { Navigate, useLocation } from 'react-router'
import { useAuth } from '../../lib/auth'
import { useSubscription } from '../../hooks/useSubscription'
import { BouncingDots } from './ui/LoadingState'

// Pages always accessible even after trial expires
const SUBSCRIPTION_EXEMPT = [
    '/seller/subscription',
    '/seller/settings',
    '/seller/login',
]

function computeTrialDaysLeft(profile: { trial_started_at?: string | null; created_at?: string } | null): number {
    if (!profile) return 60
    const started = profile.trial_started_at ?? profile.created_at
    if (!started) return 60
    const daysSince = Math.floor((Date.now() - new Date(started).getTime()) / 86_400_000)
    return Math.max(0, 60 - daysSince)
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, loading, profile } = useAuth()
    const location = useLocation()

    const trialDaysLeft = computeTrialDaysLeft(profile)
    const trialExpired = trialDaysLeft <= 0

    // Only query the subscriptions table if the trial has actually expired.
    // During the 14-day trial, skip the round-trip entirely — zero wait.
    const { isActive, loading: subLoading } = useSubscription({ skip: !trialExpired })

    const isExemptPage = SUBSCRIPTION_EXEMPT.some(p => location.pathname.startsWith(p))

    // Show loading ONLY while auth resolves (fast, comes from session cache).
    // Only additionally wait for subscription query if trial is expired.
    if (loading || (trialExpired && subLoading && !isExemptPage)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#0B6E4F] flex items-center justify-center shadow-lg animate-pulse">
                        <span className="text-white font-bold text-xl">M</span>
                    </div>
                    <BouncingDots color="#0B6E4F" size="w-2.5 h-2.5" />
                </div>
            </div>
        )
    }

    // Not logged in → login page
    if (!user) {
        return <Navigate to="/seller/login" replace />
    }

    // Trial expired + no active subscription → paywall
    if (trialExpired && !isActive && !isExemptPage) {
        return <Navigate to="/seller/subscription" replace />
    }

    return <>{children}</>
}
